import React, { useState, useEffect } from 'react';
import { Form, Col, Row, Modal, Button } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import EmployeeService from '../../repository/employeeRepository/EmployeeService';
import { strings } from '../../localization/Localization';
import { ReactComponent as EditPen } from '../../assets/icons/EditPen.svg';
import { ReactComponent as DeleteX } from '../../assets/icons/DeleteX.svg';
import { ReactComponent as PasswordChange } from '../../assets/icons/PasswordChange.svg';
import Swal from 'sweetalert2';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordEmployee, setPasswordEmployee] = useState(null);

  useEffect(() => {
  }, [showPasswordModal, passwordEmployee]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = () => {
    EmployeeService.getAllEmployees().then((response) => {
      setEmployees(response.data);
    }).catch((error) => {
      console.error('Error fetching employees:', error);
    });
  };

  function formatDateToDDMMYYYY(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${day}/${month}/${year}`;
  }

  // function formatDateForInput(dateString) {
  //   if (!dateString) return '';
  //
  //   if (dateString.includes('-')) return dateString;
  //
  //   const [day, month, year] = dateString.split('/');
  //   return `${year}-${month}-${day}`;
  // }

  const convertToDisplayDate = (date) => {
    if (!date || !date.includes('/')) return '';
    const [day, month, year] = date.split('/');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  const getValidationSchema = (isEditing) => {
    const baseSchema = {
      name: yup.string().required(strings.requiredField || 'Name is required'),
      surname: yup.string().required(strings.requiredField || 'Surname is required'),
      dateOfBirth: yup.date().required(strings.requiredField || 'Date of birth is required'),
      phoneNumber: yup.string().required(strings.requiredField || 'Phone number is required'),
      email: yup.string().email('Invalid email').required(strings.requiredField || 'Email is required'),
      username: yup.string().required(strings.requiredField || 'Username is required'),
    };

    if (!isEditing) {
      baseSchema.password = yup.string().required(strings.requiredField || 'Password is required');
    }

    return yup.object().shape(baseSchema);
  };

  const passwordChangeSchema = yup.object().shape({
    oldPassword: yup.string().required(strings.requiredField || 'Current password is required'),
    newPassword: yup.string().required(strings.requiredField || 'New password is required'),
    confirmPassword: yup.string()
      .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
      .required(strings.requiredField || 'Confirm password is required')
  });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      const {
        id,
        dateOfBirth,
        email,
        name,
        phoneNumber,
        surname,
        password,
        username
      } = values;

      const date = dateOfBirth instanceof Date
        ? formatDateToDDMMYYYY(dateOfBirth)
        : formatDateToDDMMYYYY(new Date(dateOfBirth));

      if (id) {
        await EmployeeService.updateEmployee(
          id, date, email, name, phoneNumber, surname, username
        );
      } else {
        await EmployeeService.addEmployee(
          date, email, name, phoneNumber, surname, password, username
        );
      }

      fetchEmployees();
      resetForm();
      setIsEditing(false);
      setSelectedEmployee(null);

      Swal.fire({
        icon: 'success',
        title: id ? strings.employeeUpdatedSuccessfully || 'Employee updated successfully' : strings.employeeAddedSuccessfully || 'Employee added successfully',
      });
    } catch (error) {
      console.error('Error saving employee:', error);
      Swal.fire({
        title: strings.error || 'Error!',
        text: strings.uniqueMessage,
        icon: 'error'
      });
    }
  };

  const handlePasswordChange = async (values, { resetForm }) => {
    try {
      if (!passwordEmployee) {
        console.error('No employee selected for password change');
        return;
      }

      const { oldPassword, newPassword } = values;

      await EmployeeService.updateEmployeePassword(
        passwordEmployee.id,
        oldPassword,
        newPassword
      );

      resetForm();
      setShowPasswordModal(false);
      setPasswordEmployee(null);

      Swal.fire({
        icon: 'success',
      });
    } catch (error) {
      console.error('Error changing password:', error);
      Swal.fire({
        title: strings.error || 'Error!',
        icon: 'error'
      });
    }
  };

  const handleDeleteEmployee = async (id) => {
    try {
      const result = await Swal.fire({
        title: strings.confirmationDelete,
        icon: 'warning',
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: strings.submit,
        cancelButtonText: strings.cancel,
        dangerMode: true,
      });

      if (result.isConfirmed) {
        await EmployeeService.deleteEmployee(id);
        fetchEmployees();
        Swal.fire({
          icon: 'success',
          title: strings.employeeDeletedSuccessfully,
        });
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      Swal.fire({
        title: strings.somethingWentWrong,
        icon: 'error',
        dangerMode: true
      });
    }
  };

  const handleEditEmployee = (employee) => {
    const formattedEmployee = {
      ...employee,
      dateOfBirth: employee.dateOfBirth ? convertToDisplayDate(employee.dateOfBirth) : ''
    };
    setSelectedEmployee(formattedEmployee);
    setIsEditing(true);
  };

  const handleOpenPasswordModal = (employee) => {
    try {
      setPasswordEmployee(employee);
      setShowPasswordModal(true);
      setTimeout(() => {
      }, 100);
    } catch (error) {
      console.error('Error opening password modal:', error);
    }
  };

  const handleCancel = (resetForm) => {
    resetForm();
    setSelectedEmployee(null);
    setIsEditing(false);
  };

  const formatRole = (role) => {
    if (role === 'ROLE_USER') return 'User';
    if (role === 'ROLE_ADMIN') return 'Admin';
    return role;
  };

  const fields = [
    {
      name: 'name',
      label: strings.name || 'Name',
      type: 'text',
      placeholder: 'John'
    },
    {
      name: 'surname',
      label: strings.surname || 'Surname',
      type: 'text',
      placeholder: 'Doe'
    },
    {
      name: 'dateOfBirth',
      label: strings.dateOfBirth || 'Date of Birth',
      type: 'date',
      placeholder: '01/01/1990'
    },
    {
      name: 'phoneNumber',
      label: strings.phoneNumber || 'Phone Number',
      type: 'text',
      placeholder: '+1234567890'
    },
    {
      name: 'email',
      label: strings.email || 'Email',
      type: 'email',
      placeholder: 'john.doe@example.com'
    },
    {
      name: 'username',
      label: strings.username || 'Username',
      type: 'text',
      placeholder: 'johndoe'
    },
    {
      name: 'password',
      label: strings.password || 'Password',
      type: 'password',
      placeholder: '********',
      showOnlyForNew: true
    }
  ];

  const renderFields = (fields, values, handleChange, errors, touched, isEditing) =>
    fields.map(({
      name,
      label,
      type,
      placeholder,
      showOnlyForNew
    }) => {
      if (showOnlyForNew && isEditing) return null;

      if (name === 'dateOfBirth') {
        return (
          <Form.Group as={Col} md='12' controlId={`validationFormik_${name}`} key={name}>
            <Form.Label>{label}</Form.Label>
            <ReactDatePicker
              selected={values[name] ? new Date(values[name]) : null}
              onChange={(date) => handleChange({
                target: {
                  name,
                  value: date
                }
              })}
              dateFormat='dd/MM/yyyy'
              className={`form-control ${touched[name] && errors[name] ? 'is-invalid' : ''}`}
              wrapperClassName='w-100'
              placeholderText={placeholder}
            />
            <Form.Control.Feedback type='invalid'>{errors[name]}</Form.Control.Feedback>
          </Form.Group>
        );
      }

      return (
        <Form.Group as={Col} md='12' controlId={`validationFormik_${name}`} key={name}>
          <Form.Label>{label}</Form.Label>
          <Form.Control
            type={type}
            name={name}
            value={values[name] || ''}
            onChange={handleChange}
            isValid={touched[name] && !errors[name]}
            isInvalid={touched[name] && !!errors[name]}
            placeholder={placeholder}
          />
          <Form.Control.Feedback type='invalid'>{errors[name]}</Form.Control.Feedback>
        </Form.Group>
      );
    });

  return (
    <div className='container mt-3 mx-auto'>
      <div className='flex space-x-4'>
        <div className='row'>
          <div className={'col-8'}>
            <div className={'d-flex'}>
              <h2 className={'text-center mt-4'}>{strings.employees || 'Employees'}</h2>
              {/* <div className={"justify-content-end"}> */}
              {/*   <button onClick={exportEmployees} */}
              {/*           className="btn btn-danger text-white px-4 py-2 rounded" */}
              {/*   >Export</button> */}
              {/* </div> */}
            </div>
            <hr className={'mt-0'}/>
            <table className='table user-table table-responsive table-borderless table-striped'>
              <thead className='table-header-css'>
              <tr>
                <th></th>
                <th>{strings.username}</th>
                <th>{strings.name}</th>
                <th>{strings.surname}</th>
                <th>{strings.dateOfBirth}</th>
                <th>{strings.phoneNumber}</th>
                <th>{strings.email}</th>
                <th>{strings.role}</th>
                <th>{strings.actions}</th>
              </tr>
              </thead>
              <tbody>
              {employees.map((employee) => (
                <tr key={employee.id}>
                  <td></td>
                  <td>{employee.username}</td>
                  <td>{employee.name}</td>
                  <td>{employee.surname}</td>
                  <td>{employee.dateOfBirth}</td>
                  <td>{employee.phoneNumber}</td>
                  <td>{employee.email}</td>
                  <td>{formatRole(employee.role)}</td>
                  <td>
                    <span className='d-flex'>
                      <span className='cursor-pointer' onClick={() => handleEditEmployee(employee)}>
                        <EditPen width='25px' height='25px'/>
                      </span>
                      <span className='cursor-pointer' onClick={() => handleDeleteEmployee(employee.id)}>
                        <DeleteX width='25px' height='25px'/>
                      </span>
                      <span
                          className='cursor-pointer'
                          onClick={() => handleOpenPasswordModal(employee)}>
                        <PasswordChange width='25px' height='25px'/>
                      </span>
                    </span>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
          {/* <div className={'col-1'}></div> */}
          <div className={'col-4'}>
            <div className='bg-white rounded-lg mt-4 p-4 shadow'>
              <h4 className='mb-4'>{isEditing ? (strings.editEmployee || 'Edit Employee') : (strings.addEmployee || 'Add Employee')}</h4>

              <Formik
                validationSchema={getValidationSchema(isEditing)}
                onSubmit={handleSubmit}
                initialValues={{
                  id: selectedEmployee?.id || null,
                  name: selectedEmployee?.name || '',
                  surname: selectedEmployee?.surname || '',
                  dateOfBirth: selectedEmployee?.dateOfBirth || '',
                  phoneNumber: selectedEmployee?.phoneNumber || '',
                  email: selectedEmployee?.email || '',
                  username: selectedEmployee?.username || '',
                  password: '',
                  role: selectedEmployee?.role || ''
                }}
                enableReinitialize
              >
                {({
                  handleSubmit,
                  handleChange,
                  values,
                  touched,
                  errors,
                  resetForm
                }) => (
                  <Form noValidate onSubmit={handleSubmit}>
                    <Row className='mb-3'>
                      {renderFields(fields, values, handleChange, errors, touched, isEditing)}
                    </Row>
                    <div className='flex justify-end ms-auto'>
                      <button
                        type='submit'
                        className='btn btn-primary text-white me-2'
                      >
                        {strings.submit || 'Submit'}
                      </button>
                      {isEditing && (
                        <button
                          type='button'
                          onClick={() => handleCancel(resetForm)}
                          className='btn btn-secondary text-white'
                        >
                          {strings.cancel || 'Cancel'}
                        </button>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
        centered
        backdropClassName="modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {strings.passwordChange} - {passwordEmployee?.username}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            validationSchema={passwordChangeSchema}
            onSubmit={handlePasswordChange}
            initialValues={{
              oldPassword: '',
              newPassword: '',
              confirmPassword: ''
            }}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              touched,
              errors,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="oldPassword">
                  <Form.Label>{strings.currentPassword}</Form.Label>
                  <Form.Control
                    type="password"
                    name="oldPassword"
                    value={values.oldPassword}
                    onChange={handleChange}
                    isValid={touched.oldPassword && !errors.oldPassword}
                    isInvalid={touched.oldPassword && !!errors.oldPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.oldPassword}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="newPassword">
                  <Form.Label>{strings.newPassword}</Form.Label>
                  <Form.Control
                    type="password"
                    name="newPassword"
                    value={values.newPassword}
                    onChange={handleChange}
                    isValid={touched.newPassword && !errors.newPassword}
                    isInvalid={touched.newPassword && !!errors.newPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.newPassword}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3" controlId="confirmPassword">
                  <Form.Label>{strings.confirmPassword}</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    value={values.confirmPassword}
                    onChange={handleChange}
                    isValid={touched.confirmPassword && !errors.confirmPassword}
                    isInvalid={touched.confirmPassword && !!errors.confirmPassword}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword}
                  </Form.Control.Feedback>
                </Form.Group>

                <div className="d-flex justify-content-end">
                  <Button
                    variant="secondary"
                    className="me-2"
                    onClick={() => setShowPasswordModal(false)}
                  >
                    {strings.cancel}
                  </Button>
                  <Button variant="primary" type="submit">
                    {strings.submit}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Employees;